import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import { BreadcrumbItem } from "./Topbar";

// =============================================================================
// PortalChrome — lets a page set the shell's topbar chrome (breadcrumb / actions)
// from inside the content, without prop-drilling through the layout.
//
//   const MyPage = () => {
//     usePortalChrome({ breadcrumb: [{ label: "Customers" }, { label: name }] });
//     return <>…</>;
//   };
//
// Anything set here overrides the breadcrumb/actions passed as PortalLayout props
// (route-level defaults). Outside a PortalLayout the hook is a safe no-op.
// =============================================================================

export interface PortalChrome {
  breadcrumb?: BreadcrumbItem[];
  topbarActions?: React.ReactNode;
}

interface ChromeStore extends PortalChrome {
  setChrome: (chrome: PortalChrome) => void;
}

const PortalChromeContext = createContext<ChromeStore | null>(null);

export const PortalChromeProvider = ({ children }: { children: React.ReactNode }) => {
  const [chrome, setChrome] = useState<PortalChrome>({});
  const value = useMemo<ChromeStore>(() => ({ ...chrome, setChrome }), [chrome]);
  return <PortalChromeContext.Provider value={value}>{children}</PortalChromeContext.Provider>;
};

/** Internal: the shell reads merged chrome to feed the Topbar. */
export const usePortalChromeState = (): PortalChrome => {
  const ctx = useContext(PortalChromeContext);
  return { breadcrumb: ctx?.breadcrumb, topbarActions: ctx?.topbarActions };
};

/**
 * Set the topbar chrome from a page. Pass a stable object (the breadcrumb is
 * diffed by value; `topbarActions` changes are picked up when the breadcrumb
 * changes — for independently-dynamic actions, vary `breadcrumb` or remount).
 * Chrome is cleared automatically on unmount.
 */
export const usePortalChrome = (chrome: PortalChrome) => {
  const ctx = useContext(PortalChromeContext);
  const setChrome = ctx?.setChrome;
  const breadcrumbKey = JSON.stringify(chrome.breadcrumb ?? null);

  useEffect(() => {
    if (!setChrome) return;
    setChrome(chrome);
    return () => setChrome({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChrome, breadcrumbKey]);
};
